import React, { useState, useEffect, useRef } from 'react';
import IconSettings from '@salesforce/design-system-react/components/icon-settings';
import Modal from '@salesforce/design-system-react/components/modal';
import Button from '@salesforce/design-system-react/components/button';
import ColorPicker from '@salesforce/design-system-react/components/color-picker';
import Textarea from '@salesforce/design-system-react/components/textarea';
import Input from '@salesforce/design-system-react/components/input';
import { Formik } from 'formik';
import { addStandardUserValidation } from './addStandardUserValidation';
import { addNewStandardUser, standardUsersSelector } from '../../store/standardUsers/slice';
import { useDispatch, useSelector } from 'react-redux';
import toast, { Toaster } from 'react-hot-toast';
import { getHedgeFundStandardUsersMain } from '../../store/hedgeFunds/slice';
import Combobox from '@salesforce/design-system-react/components/combobox';
import comboboxFilterAndLimit from '@salesforce/design-system-react/components/combobox/filter';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const defaultTagOptions = [
  {
    id: 'trading',
    label: 'Trading',
  },
  {
    id: 'skating',
    label: 'Skating',
  },
  {
    id: 'tomb',
    label: 'Tomb',
  },
];

const AddStandardUser = (props) => {
  const { openModal, closeModal } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [formData, setFormData] = useState({});
  const [tagSelection, setTagSelection] = useState([]);
  const [tagInputText, setTagInputText] = useState('');
  const dispatch = useDispatch();
  const { loading, success, message } = useSelector(standardUsersSelector);
  const formikRef = useRef();

  useEffect(() => {
    setIsOpen(openModal);
  }, [openModal]);

  useEffect(() => {
    success &&
      showToast &&
      toast.success(message, {
        id: 'hedge-fund-standard-user',
      });
    setShowToast(false);
  }, [success, showToast, message]);

  const toggleOpen = (res) => {
    formikRef.current?.resetForm();
    setFormData({});
    setTagSelection([]);
    closeModal({
      open: false,
    });
    res ? setShowToast(true) : setShowToast(false);
  };

  return (
    <Formik
      innerRef={formikRef}
      enableReinitialize={true}
      // validateOnMount={true}
      initialValues={{
        firstName: '',
        lastName: '',
        email: '',
        meta_mask_address: '',
        contactNumber: '',
        address: '',
        description: '',
        tags: '',
        color: '',
      }}
      validationSchema={addStandardUserValidation}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        // setShowToast(true);
        setSubmitting(true);
        const data = {
          name: values.firstName,
          first_name: values.firstName,
          last_name: values.lastName,
          email: values.email,
          meta_mask_address: values.meta_mask_address,
          address: values.address,
          contact_number: values.contactNumber,
          description: values.description,
          tags: values.tags,
          color: values.color,
          role: 'user',
          account_type: 'organization',
        };
        dispatch(addNewStandardUser(data)).then((res) => {
          if (res?.payload) {
            dispatch(getHedgeFundStandardUsersMain());
            toggleOpen(res);
          }
        });
      }}
    >
      {(props) => {
        const {
          values,
          touched,
          errors,
          isSubmitting,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
        } = props;
        return (
          <div>
            <Modal
              ariaHideApp={false}
              isOpen={isOpen}
              footer={[
                <Button
                  className="modal-cancel-button"
                  key="cancel"
                  label="Cancel"
                  onClick={toggleOpen}
                />,
                <IconSettings iconPath="/assets/icons">
                  <Button
                    className="modal-save-button"
                    key="save"
                    label={loading ? <span>Loading</span> : <span>Save</span>}
                    disabled={isSubmitting}
                    onClick={handleSubmit}
                    iconCategory="utility"
                    iconName="save"
                    iconPosition="left"
                  />
                </IconSettings>,
              ]}
              onRequestClose={toggleOpen}
              heading="Add Hedgefund User"
            >
              <section className="slds-p-around_large">
                <form
                  onSubmit={handleSubmit}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      handleSubmit();
                    }
                  }}
                >
                  <Toaster position="top-right" reverseOrder={false} />
                  <div className="slds-grid slds-grid_pull-padded slds-grid_vertical-align-center">
                    <div className="slds-col_padded slds-m-bottom_large">
                      <Input
                        id="firstName"
                        name="firstName"
                        label="First Name"
                        placeholder="John"
                        required
                        value={values.firstName || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={classNames(
                          errors.firstName && touched.firstName && 'slds-has-error'
                        )}
                      />
                      {errors.firstName && touched.firstName && (
                        <div className="slds-form-element__help">{errors.firstName}</div>
                      )}
                    </div>
                    <div className="slds-col_padded slds-m-bottom_large">
                      <Input
                        id="lastName"
                        name="lastName"
                        label="Last Name"
                        placeholder="Dow"
                        required
                        value={values.lastName || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={classNames(
                          errors.lastName && touched.lastName && 'slds-has-error'
                        )}
                      />
                      {errors.lastName && touched.lastName && (
                        <div className="slds-form-element__help">{errors.lastName}</div>
                      )}
                    </div>
                  </div>
                  <div className="slds-grid slds-grid_pull-padded slds-grid_vertical-align-center">
                    <div className="slds-col_padded slds-m-bottom_large">
                      <Input
                        id="email"
                        name="email"
                        label="Email"
                        placeholder="johndoe@admin.com"
                        required
                        value={values.email || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={classNames(errors.email && touched.email && 'slds-has-error')}
                      />
                      {errors.email && touched.email && (
                        <div className="slds-form-element__help">{errors.email}</div>
                      )}
                    </div>
                    <div className="slds-col_padded slds-m-bottom_large">
                      <Input
                        id="meta_mask_address"
                        name="meta_mask_address"
                        label="Meta Mask Public Address"
                        placeholder="Public Address"
                        required
                        value={values.meta_mask_address || ''}
                        onBlur={handleBlur}
                        className={classNames(
                          errors.meta_mask_address && touched.meta_mask_address && 'slds-has-error'
                        )}
                        onChange={handleChange}
                        onKeyDown={(e) => {
                          e.key === 'Enter' && e.preventDefault();
                        }}
                      />
                      {errors.meta_mask_address && touched.meta_mask_address && (
                        <div className="slds-form-element__help">{errors.meta_mask_address}</div>
                      )}
                    </div>
                  </div>
                  <div className="slds-grid slds-grid_pull-padded slds-grid_vertical-align-center">
                    <div className="slds-col_padded slds-m-bottom_large">
                      <Input
                        id="address"
                        name="address"
                        label="Address"
                        placeholder="ADGM, Abu Dahbi, UAE"
                        value={values.address || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={classNames(
                          errors.address && touched.address && 'slds-has-error'
                        )}
                      />
                      {errors.address && touched.address && (
                        <div className="slds-form-element__help">{errors.address}</div>
                      )}
                    </div>
                    <div className="slds-col_padded slds-m-bottom_large">
                      <Input
                        id="contactNumber"
                        name="contactNumber"
                        label="Contact Number"
                        placeholder="+0 123 456 7890"
                        required
                        value={values.contactNumber || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={classNames(
                          errors.contactNumber && touched.contactNumber && 'slds-has-error'
                        )}
                      />
                      {errors.contactNumber && touched.contactNumber && (
                        <div className="slds-form-element__help">{errors.contactNumber}</div>
                      )}
                    </div>
                  </div>
                  <div className="slds-grid slds-grid_pull-padded slds-grid_vertical-align-center">
                    <div className="slds-col_padded slds-m-bottom_large">
                      <IconSettings iconPath="/assets/icons">
                          <Combobox
                            id="tags"
                            name="tags"
                            optionsAddItem={
                              tagInputText.length > 0
                                ? [
                                    {
                                      id: 'add-new-tag',
                                      label: (inputValue) => [
                                        inputValue && inputValue.length > 0 ? (
                                          <span style={{ marginLeft: '-30px' }}>
                                            Create "{inputValue}"
                                          </span>
                                        ) : (
                                          ''
                                        ),
                                      ],
                                    },
                                  ]
                                : []
                            }
                            events={{
                              onChange: (event, data) => {
                                setTagInputText(data.value);
                              },
                              onSelect: (event, data) => {
                                const { selection } = data;
                                if (selection && selection.length > 0) {
                                  if (
                                    selection[selection.length - 1].type &&
                                    selection[selection.length - 1].type === 'footer'
                                  ) {
                                    if (tagInputText.length > 0) {
                                      setTagSelection(tagSelection.concat({ label: tagInputText }));
                                      setTagInputText('');
                                    }
                                  } else {
                                    setTagSelection(selection);
                                  }
                                } else {
                                  setTagSelection(selection.pop());
                                }
                              },
                              onSubmit: (event, data) => {
                                if (!data.value.trim()) return;
                                setTagSelection(tagSelection.concat({ label: data.value }));
                                setTagInputText('');
                              },
                              onRequestRemoveSelectedOption: (event, data) => {
                                setTagSelection(data.selection);
                              },
                            }}
                            labels={{
                              label: 'Tags',
                              placeholder: 'Enter tags',
                            }}
                            options={comboboxFilterAndLimit({
                              inputValue: tagInputText,
                              limit: 5,
                              options: defaultTagOptions,
                              selection: tagSelection,
                            })}
                            value={tagInputText}
                            selection={tagSelection}
                            multiple
                          />
                      </IconSettings> 
                      {errors.tags && touched.tags && (
                        <div className="slds-form-element__help">{errors.tags}</div>
                      )}
                    </div>
                    <div className="slds-col_padded">
                      <div className="slds-m-bottom_large">
                        <div className="">
                          <IconSettings iconPath="/assets/icons">
                            <ColorPicker
                              labels={{ label: 'Color' }}
                              id="color"
                              name="color"
                              menuPosition="overflowBoundaryElement"
                              value=""
                              events={{
                                onChange: (event, { color }) => {
                                  setFieldValue('color', color);
                                  setFormData({
                                    ...formData,
                                    color,
                                  });
                                },
                              }}
                            />
                          </IconSettings>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="slds-grid slds-grid_pull-padded slds-grid_vertical-align-center">
                    <div className="slds-col_padded" style={{ alignSelf: 'normal' }}>
                      <div className="slds-form-element">
                        <div className="slds-form-element__control">
                          <Textarea
                            id="description"
                            name="description"
                            label="Description"
                            placeholder="Enter description"
                            value={values.description || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </section>
            </Modal>
          </div>
        );
      }}
    </Formik>
  );
};

export default AddStandardUser;
